import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Select, Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '@warmancer/ui';
import { auditApi } from '../api/client';
import { formatTimestamp } from '../utils/formatTimestamp';
import styles from './AuditLog.module.css';

export function AuditLog() {
  const [action, setAction] = useState('');
  const [page, setPage] = useState(0);
  const limit = 25;

  const { data: actionsData } = useQuery({
    queryKey: ['audit-actions'],
    queryFn: () => auditApi.getActions(),
  });

  const { data, isLoading } = useQuery({
    queryKey: ['audit', { action, limit, offset: page * limit }],
    queryFn: () =>
      auditApi.list({
        action: action || undefined,
        limit,
        offset: page * limit,
      }),
  });

  const actionOptions = [
    { value: '', label: 'All Actions' },
    ...(actionsData?.actions.map((a) => ({ value: a, label: a })) || []),
  ];

  const totalPages = Math.ceil((data?.pagination.total || 0) / limit);

  const formatDetails = (details: Record<string, unknown> | null) => {
    if (!details) return '-';
    return Object.entries(details)
      .map(([key, value]) => `${key}: ${JSON.stringify(value)}`)
      .join(', ');
  };

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>Audit Log</h1>

      <div className={styles.filters}>
        <Select
          name="action"
          options={actionOptions}
          value={action}
          onChange={(e) => {
            setAction(e.target.value);
            setPage(0);
          }}
        />
      </div>

      <div className={styles.tableWrapper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableHeader>Timestamp</TableHeader>
              <TableHeader>Role</TableHeader>
              <TableHeader>Action</TableHeader>
              <TableHeader>Target</TableHeader>
              <TableHeader>Details</TableHeader>
              <TableHeader>IP</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.entries.map((entry) => (
              <TableRow key={entry.id}>
                <TableCell>
                  <span className={styles.timestamp}>
                    {formatTimestamp(entry.created_at)}
                  </span>
                </TableCell>
                <TableCell>
                  <span className={`${styles.role} ${styles[`role${entry.admin_role}`]}`}>
                    {entry.admin_role}
                  </span>
                </TableCell>
                <TableCell>
                  <span className={styles.action}>{entry.action}</span>
                </TableCell>
                <TableCell>
                  {entry.target_type ? (
                    <span className={styles.target}>
                      {entry.target_type}:{entry.target_id?.slice(0, 8)}...
                    </span>
                  ) : (
                    '-'
                  )}
                </TableCell>
                <TableCell>
                  <span className={styles.details} title={formatDetails(entry.details)}>
                    {formatDetails(entry.details).slice(0, 50)}
                    {formatDetails(entry.details).length > 50 && '...'}
                  </span>
                </TableCell>
                <TableCell>
                  <span className={styles.ip}>{entry.ip_address || '-'}</span>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {isLoading && <div className={styles.loading}>Loading...</div>}
        {!isLoading && !data?.entries.length && (
          <div className={styles.empty}>No audit entries found</div>
        )}
      </div>

      {totalPages > 1 && (
        <div className={styles.pagination}>
          <button
            className={styles.pageBtn}
            disabled={page === 0}
            onClick={() => setPage((p) => p - 1)}
          >
            Previous
          </button>
          <span className={styles.pageInfo}>
            Page {page + 1} of {totalPages}
          </span>
          <button
            className={styles.pageBtn}
            disabled={page >= totalPages - 1}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
